import { createContext, useContext } from "react";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../../context/API_Client";
import { kpiCalls } from "../KPICalls";
import { customToast } from "../../../reusableComponents/customToaster/customToast";
import {
  getCalendarInterval,
  getCalendarIntervalForGraph,
  getCustomDateRange,
  getUTCDayRange,
  getUTCDayRangePercentage,
  isEmpty,
} from "../../utils/utils";
import Overview from "./overview";
import {
  updateAllCountrySchemaArray,
  updateAllCurrencySchemaArray,
  updateAllPspSchemaArray,
  updatePaymentMethodsArray,
} from "../../../reusableComponents/filterUtils";
import {
  overviewFilterSchema,
  overviewFilterSchemaForFailureReason,
} from "../schema/overviewSchema/overviewFilterSchema";
import { failedTransactionSchema } from "./failedTransactionsSchema";

const OverviewAnalyticsContext = createContext();

export const OverviewAnalyticsProvider = ({
  selectedBusinessUnit,
  selectedDropdownValue,
  selectedFilterCurrency,
  refresh,
  singleStartDate,
  startDate,
  endDate,
}) => {
  const {
    getAllVisualizationData,
    getAllPaymentMethods,
    getPaymentMethod,
    merchantSettings,
    getAllPspConfigs,
    getAllModalData,
  } = useAuth();

  const [totalPayment, setTotalPayment] = useState({});
  const [totalAmount, setTotalAmount] = useState({});
  const [averageTicketSize, setAverageTicketSize] = useState({});
  const [successRate, setSuccessRate] = useState({});
  const [locale, setLocale] = useState("");
  const [logo, setLogo] = useState([]);
  const [paymentData, setPaymentData] = useState();
  const [gmvPspData, setGmvPspData] = useState();
  const [withSearchbarModalDataForGraph, setWithSearchbarModalDataForGraph] =
    useState([]);
  const [chartTransactionOverAll, setChartTransactionOverAll] = useState();
  const [volumeTransactionOverAll, setVolumeTransactionOverAll] = useState();
  const [
    withSearchbarModalDataForFailureReason,
    setWithSearchbarModalDataForFailureReason,
  ] = useState([]);
  const [failureReasonTableData, setFailureReasonTableData] = useState(null);
  const [failureReasonChartData, setFailureReasonChartData] = useState(null);

  // KPI calls

  const resetKips = () => {
    setTotalAmount({})
    setTotalPayment({})
    setSuccessRate({})
    setAverageTicketSize({})
    setChartTransactionOverAll()
    setVolumeTransactionOverAll()
    setFailureReasonChartData(null)
    setFailureReasonTableData(null)
    setGmvPspData()
  }

  useEffect(() => {
    if (
      (selectedDropdownValue &&
        selectedFilterCurrency) ||
      singleStartDate ||
      (startDate && endDate)
    ) {
      resetKips()
      kpiCalls(
        getAllVisualizationData,
        selectedDropdownValue,
        {
          url: "overall_gmv_count_aggs_report",
          range: "today",
          offset: 0,
          title: "Successful Transactions",
          fieldName: "totalCount",
          attributeFilters: {
            operationName: ["Authorisation", "Sale"],
            operationStatus: "Approved",
            legalEntity: selectedBusinessUnit,
            currencyCode: selectedFilterCurrency,
          },
          localeFieldName: "countLocale",
          // localeFieldName: "countLocale",
          singleStartDate: singleStartDate,
          startDate: startDate,
          endDate: endDate,
          tooltipContent:
            "Total number of successful transactions which includes uncaptured transactions as well for a particular period.",
        },
        (data) => setTotalPayment(data),
        (errMsg) => customToast("error", "high", errMsg)
      );
      kpiCalls(
        getAllVisualizationData,
        selectedDropdownValue,
        {
          url: "overall_gmv_count_aggs_report",
          range: "today",
          offset: 0,
          title: "Transactional Value",
          fieldName: "gmv",
          attributeFilters: {
            operationStatus: "Approved",
            operationName: ["Authorisation", "Sale"],
            currencyCode: selectedFilterCurrency,
            legalEntity: selectedBusinessUnit,
          },
          localeFieldName: "gmvLocaleFull",
          // localeFieldName: "gmvLocale",
          singleStartDate: singleStartDate,
          startDate: startDate,
          endDate: endDate,
          tooltipContent:
            "Total value of successful transactions which includes uncaptured amounts as well for a particular period",
        },
        (data) => setTotalAmount(data),
        (errMsg) => customToast("error", "high", errMsg)
      );
      kpiCalls(
        getAllVisualizationData,
        selectedDropdownValue,
        {
          url: "rerouted_transactions_gmv_report",
          range: "today",
          offset: 0,
          title: "Transactions Saved",
          fieldName: "amount",
          attributeFilters: {
            operationStatus: "Approved",
            operationName: ["Authorisation", "Sale"],
            legalEntity: selectedBusinessUnit,
            currencyCode: selectedFilterCurrency,
          },
          localeFieldName: "amountLocaleFull",
          singleStartDate: singleStartDate,
          startDate: startDate,
          endDate: endDate,
          tooltipContent:
            "Total value of orders that have been saved via routing transactions in the downtime.",
        },
        (data) => setAverageTicketSize(data),
        (errMsg) => customToast("error", "high", errMsg)
      );
      kpiCalls(
        getAllVisualizationData,
        selectedDropdownValue,
        {
          url: "success_rate_overall_aggs_report",
          range: "today",
          offset: 0,
          title: "Success Rate",
          fieldName: "successRate",
          attributeFilters: {
            operationName: ["Authorisation", "Sale"],
            legalEntity: selectedBusinessUnit,
            currencyCode: selectedFilterCurrency,
          },
          localeFieldName: "successRate",
          showAbsoluteDifference: true,
          singleStartDate: singleStartDate,
          startDate: startDate,
          endDate: endDate,
          tooltipContent:
            "Successful transaction divided by the total transactions for a particular period.",
        },

        (data) => setSuccessRate(data),
        (errMsg) => customToast("error", "high", errMsg)
      );
    }
  }, [
    selectedBusinessUnit,
    selectedDropdownValue,
    selectedFilterCurrency,
    refresh,
    singleStartDate,
  ]);

  useEffect(() => {
    merchantSettings(
      function (data) {
        setLocale(data?.locale);
      },
      function (errorMsg) {
        customToast("error", "high", errorMsg);
      }
    );
    getAllPspConfigs(
      function (data) {
        setLogo(data);
      },
      function (errorMsg) {
        customToast("error", "high", errorMsg);
      }
    );

    getPaymentMethod(
      function (paymentData) {
        setPaymentData(paymentData);
      },
      function (errorMsg) {
        customToast("error", "high", errorMsg);
      }
    );
  }, []);

  // payment method and categories
  useEffect(() => {
    const calendarInterval = getCalendarInterval(selectedDropdownValue);
    const dateRange =
      selectedDropdownValue === "customDate"
        ? getCustomDateRange(singleStartDate, startDate, endDate)
        : getUTCDayRange(selectedDropdownValue);
    const processorAndMethodPaymentsPayload = {
      ...dateRange,
      calendarInterval: calendarInterval,
      attributeFilters: {
        operationStatus: "Approved",
        operationName: ["Authorisation", "Sale"],
        currencyCode: selectedFilterCurrency,
        legalEntity: selectedBusinessUnit,
      },
    };
    if (
      selectedDropdownValue === "customDate" &&
      !singleStartDate &&
      (!startDate || !endDate)
    ) {
      return;
    }

    if (
      (selectedDropdownValue &&
        selectedBusinessUnit?.length > 0 &&
        selectedFilterCurrency) ||
      singleStartDate ||
      (startDate && endDate)
    ) {
      getAllVisualizationData(
        "gmv_psp_aggs_report",
        processorAndMethodPaymentsPayload,
        function (gmvData) {
          let data = gmvData?.points;
          data.map((point) => {
            point["gmvLocale_sort"] = point?.gmv;
            return point;
          });
          setGmvPspData(gmvData?.points);
        },
        function (errorMsg) {
          customToast("error", "high", errorMsg);
        }
      );
    }
  }, [
    selectedDropdownValue,
    refresh,
    selectedBusinessUnit,
    selectedFilterCurrency,
    singleStartDate,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    if (
      selectedDropdownValue === "customDate" &&
      !singleStartDate &&
      (!startDate || !endDate)
    ) {

      return;
    }

    if (
      (selectedDropdownValue &&
        selectedBusinessUnit?.length > 0 &&
        selectedFilterCurrency) ||
      singleStartDate ||
      (startDate && endDate)
    ) {
      const calendarInterval = getCalendarIntervalForGraph(
        selectedDropdownValue
      );
      const dateRange =
        selectedDropdownValue === "customDate"
          ? getCustomDateRange(singleStartDate, startDate, endDate)
          : getUTCDayRange(selectedDropdownValue);
      const attributeFilters = {
        operationStatus: "Approved",
        operationName: ["Authorisation", "Sale"],
        currencyCode: selectedFilterCurrency,
        legalEntity: selectedBusinessUnit,
      };
      withSearchbarModalDataForGraph.forEach((item) => {
        switch (item?.id) {
          case "pspCode":
            attributeFilters.pspCode = item?.value;
            break;
          case "paymentMethodType":
            attributeFilters.paymentMethodType = item?.value;
            break;
          default:
            break;
        }
      });
      getAllVisualizationData(
        "transactions_overall_report",
        {
          ...dateRange,
          calendarInterval: calendarInterval,
          attributeFilters: attributeFilters,
        },
        function (chartTransactionOverAll) {
          setChartTransactionOverAll(chartTransactionOverAll?.points);
        },
        function (errorMsg) {
          customToast("error", "high", errorMsg);
        }
      );

      getAllVisualizationData(
        "gmv_overall_report",
        {
          ...dateRange,
          calendarInterval: calendarInterval,
          attributeFilters: attributeFilters,
        },
        function (volumeTransactionOverAll) {
          setVolumeTransactionOverAll(volumeTransactionOverAll?.points);
        },
        function (errorMsg) {
          customToast("error", "high", errorMsg);
        }
      );
    }
  }, [
    withSearchbarModalDataForGraph,
    selectedDropdownValue,
    refresh,
    selectedBusinessUnit,
    selectedFilterCurrency,
    singleStartDate,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    getAllModalData(
      function (data) {
        let schema = overviewFilterSchema;
        updateAllCurrencySchemaArray(
          schema,
          "currencyCode",
          data?.currencyCodes
        );

        updateAllCountrySchemaArray(schema, "countryCode", data?.countryCodes);

        updateAllPspSchemaArray(schema, "pspCode", data?.pspCodes);
        updateAllPspSchemaArray(
          overviewFilterSchemaForFailureReason,
          "pspCode",
          data?.pspCodes
        );

        getAllPaymentMethods(
          function (methodData) {
            const merchantPaymentMethods = methodData.filter((item) =>
              data?.paymentMethodIds.includes(item?.id)
            );
            updatePaymentMethodsArray(
              schema,
              "paymentMethodType",
              merchantPaymentMethods
            );
          },
          function (errorMsg) {
            customToast("error", "high", errorMsg);
          }
        );
      },
      function (errorMsg) {
        customToast("error", "high", errorMsg);
      }
    );
  }, []);

  useEffect(() => {
    if (
      selectedDropdownValue === "customDate" &&
      !singleStartDate &&
      (!startDate || !endDate)
    ) {
      return;
    }

    if (
      (selectedDropdownValue &&
        selectedBusinessUnit?.length > 0 &&
        selectedFilterCurrency) ||
      singleStartDate ||
      (startDate && endDate)
    ) {
      const calendarInterval = getCalendarInterval(selectedDropdownValue);
      const dateRange =
        selectedDropdownValue === "customDate"
          ? getCustomDateRange(singleStartDate, startDate, endDate)
          : getUTCDayRange(selectedDropdownValue);
      const attributeFilters = {
        operationStatus: ["Failed", "Rejected"],
        operationName: ["Authorisation", "Sale"],
        currencyCode: selectedFilterCurrency,
        legalEntity: selectedBusinessUnit,
      };

      withSearchbarModalDataForFailureReason.forEach((item) => {
        switch (item?.id) {
          case "pspCode":
            attributeFilters.pspCode = item.value;
            break;
          case "paymentMethodType":
            attributeFilters.paymentMethodType = item.value;
            break;
          default:
            break;
        }
      });

      // for the left side graph
      getAllVisualizationData(
        "status_reason_aggs_report",
        {
          ...dateRange,
          calendarInterval: calendarInterval,
          attributeFilters: attributeFilters,
        },
        function (data) {
          let chartData = failedTransactionSchema(
            data?.points,
            data?.metrics,
            null,
            null
          );
          setFailureReasonChartData(chartData);
          let tableData = failedTransactionSchema(
            null,
            null,
            data?.points,
            data?.metrics
          );
          setFailureReasonTableData(tableData);
        },
        function (errorMsg) {
          customToast("error", "high", errorMsg);
        }
      );
    }
  }, [
    selectedDropdownValue,
    withSearchbarModalDataForFailureReason,
    refresh,
    selectedBusinessUnit,
    selectedFilterCurrency,
    singleStartDate,
    startDate,
    endDate,
  ]);

  return (
    <OverviewAnalyticsContext.Provider
      value={{
        averageTicketSize,
        successRate,
        totalAmount,
        totalPayment,
        locale,
        logo,
        paymentData,
        gmvPspData,
        setWithSearchbarModalDataForGraph,
        setWithSearchbarModalDataForFailureReason,
        chartTransactionOverAll,
        volumeTransactionOverAll,
        failureReasonChartData,
        failureReasonTableData,
        singleStartDate,
        startDate,
        endDate,
      }}
    >
      <Overview />
    </OverviewAnalyticsContext.Provider>
  );
};

export const OverviewAnalyticsStates = () => {
  return useContext(OverviewAnalyticsContext);
};
